import { appSettings } from '../AppSettings';
import { Sample } from './SampleData';
import { Dictionary } from '../util/TypeUtil';
import { BaseGet, BasePost } from './BaseData';
import { Transition } from './SampleTrackingTransitionData';
import { DefaultExhaustionApproach } from './ReferenceData';
import { ResearchProjectPatient, ResearchProjectSample } from './ResearchProjectSelectionData';
import { InformaticsQcMetrics } from './InformaticsQcData';

export type ParentExhaustion = DefaultExhaustionApproach;

export interface SampleTrackingCounts {
  sampleGroup: string;
  sampleCounts: Dictionary<Dictionary<number>>;
  patientCounts: Dictionary<Dictionary<number>>;
}

export interface SampleTrackingCountsByResearchProject {
  researchProjectId: string;
  sampleCounts: Dictionary<number>;
  patientCounts: Dictionary<number>;
}

export interface TransitionSample {
  transitionSampleId: string;
  transitionId: string;
  sampleId: string;
  labAssignedSampleId?: string;
  transitionQualityCheckGroupId?: string;
  enteredAt: Date;
  exitedAt?: Date;
  qualityCheckStatus: string;
  checkByFailedReason?: string;
  qualityCheckCreatedAt?: Date;
}

export interface SampleTrackingTableData {
  sample: Sample;
  transitionSample: TransitionSample;
  antecedentSample?: Sample;
  researchProjectSample?: ResearchProjectSample;
  parentTransitionSampleId?: string;
}

export interface SampleTrackingSelectedTableData {
  sample: Sample;
  researchProjectPatient?: ResearchProjectPatient;
  researchProjectSample?: ResearchProjectSample;
  transition?: Transition;
  transitionSample?: TransitionSample;
}

export interface SampleTrackingIdMapping {
  index: string;
  sampleIdentifier: string;
  labAssignedSampleId?: string;
  qualityCheckStatus: string;
  checkByFailedReason?: string;
  r1FastQLocation?: string;
  r2FastQLocation?: string;
  errorMessage?: IdMappingErrorMessageType[];
  warningMessage?: IdMappingErrorMessageType[];
}

export interface SampleTrackingCheckByValidation {
  sampleIdentifier: string;
  labAssignedSampleId?: string;
  qualityCheckStatus: string;
}

export interface SampleTrackingCheckByFastqValidation {
  sampleIdentifier: string;
  labAssignedSampleId?: string;
  r1FastQLocation?: string;
  r2FastQLocation?: string;
  transitionId: string;
}

export interface SampleTrackingCheckByFastqValidationResult {
  transitionId: string;
  sequencingLabId: string;
  sampleIdentifier: string;
  labAssignedSampleId?: string;
  r1FastQLocation?: string;
  r2FastQLocation?: string;
  r1FastQLocationRecommendation?: string;
  r2FastQLocationRecommendation?: string;
  existingSequenceRun?: SequenceRun;
}

export interface SampleTrackingCheckByFastqValidationResultPayload {
  validated: ReadonlyArray<SampleTrackingCheckByFastqValidationResult>;
  possibleFiles: string[];
}

export interface SequenceRun {
  sequenceId: string;
  bbid: string;
  opaqueIdentifier: string;
  batchId: string;
  batchIdentifier: string;
  sequenceGroupId: string;
  sequenceGroupName: string;
  sequenceGroupPurpose: string;
  tissueTypeId: number;
  libraryTypeId: string;
  tissueTypeName: string;
  capabilityIsAvailable: boolean;
  sequencingLabId: string;
  sequencingLabName: string;
  instrumentId: number;
  instrumentName: string;
  fastqR1Path: string;
  fastqR2Path?: string;
  versionNumber: string;
}

export interface SampleTrackingCheckBy {
  originalSampleId: string;
  sampleIdentifier: string;
  labAssignedSampleId: string;
  qualityCheckStatus: string;
  checkByFailedReason?: string;
  r1FastQLocation?: string;
  r2FastQLocation?: string;
}

export interface SampleTrackingCheckByValidationResult {
  sampleIdentifier: string;
  labAssignedSampleId: string;
  mappedToSampleId?: string;
  mappedToSampleIdentifier?: SampleIdentifier;
  mappedToSampleCurrentTransition?: Transition;
  mappedFromSampleId?: string;
  mappedFromSampleIdentifier?: SampleIdentifier;
  mappedFromSampleCurrentTransition?: Transition;
  mappedFromSampleCurrentTransitionSample?: TransitionSample;
  qualityCheckStatus: string;
}

export interface SampleIdentifier {
  labAssignedSampleId?: string;
  sampleBbid?: string;
  sourceKey?: string;
  sampleId: string;
}

export type IdMappingErrorMessageType =
  | 'missingOriginalId'
  | 'missingNewLabAssignedId'
  | 'missingQualityCheckStatus'
  | 'invalidQualityCheckStatus'
  | 'tooManyParsedItemsDefault'
  | 'tooManyParsedItemsSequencing'
  | 'invalidCheckByFailedReason'
  | undefined;

export type TransitionType =
  | 'Shipping'
  | 'Receiving'
  | 'Subsetting'
  | 'Extraction'
  | 'LibraryPrep'
  | 'Sequencing'
  | 'Informatics'
  | 'ReBiobanking'
  | 'ReadyForDelivery'
  | 'Delivered'
  | 'Generic'
  | 'GenericWithSampleOutput';

export type QualityCheckStatus = 'NotYetDecided' | 'Passed' | 'Failed';

export const QualityCheckStatuses: QualityCheckStatus[] = ['NotYetDecided', 'Passed', 'Failed'];
export const PersistableQualityCheckStatuses: QualityCheckStatus[] = ['Passed', 'Failed'];

export async function GetSampleTrackingCountsBySampleType(
  researchProjectId: string,
  accessToken: string
): Promise<ReadonlyArray<SampleTrackingCounts>> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/SampleTracking/BySampleGroup/ResearchProjectId/${researchProjectId}`,
    accessToken
  );
}

export async function GetSampleTrackingSelectedSamples(
  researchProjectId: string,
  accessToken: string,
  decrypt: boolean = false,
  sampleGroupConstraint?: string
): Promise<ReadonlyArray<SampleTrackingSelectedTableData>> {
  const url = !sampleGroupConstraint
    ? `${appSettings.api.endpoint}/api/v2/SampleTracking/SelectedSamples/${researchProjectId}?decrypt=${decrypt}`
    : `${appSettings.api.endpoint}/api/v2/SampleTracking/SelectedSamples/${researchProjectId}?sampleGroupConstraint=${sampleGroupConstraint}&decrypt=${decrypt}`;

  return await BaseGet(url, accessToken);
}

export async function GetSampleTrackingTransitionSamples(
  researchProjectId: string,
  accessToken: string,
  configuredTransitionId: string,
  decrypt: boolean = false,
  sampleGroupConstraint?: string
): Promise<ReadonlyArray<SampleTrackingTableData>> {
  const url = !sampleGroupConstraint
    ? `${appSettings.api.endpoint}/api/v2/SampleTracking/${researchProjectId}/${configuredTransitionId}?decrypt=${decrypt}`
    : `${appSettings.api.endpoint}/api/v2/SampleTracking/${researchProjectId}/${configuredTransitionId}?sampleGroupConstraint=${sampleGroupConstraint}&decrypt=${decrypt}`;

  return await BaseGet(url, accessToken);
}

export async function CreateQualityCheck(
  researchProjectId: string,
  configuredTransitionId: string,
  idMappings: ReadonlyArray<SampleTrackingCheckBy>,
  parentExhaustion: ParentExhaustion,
  accessToken?: string
): Promise<string> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleTracking/CheckBy/ResearchProjectId/${researchProjectId}/Step/${configuredTransitionId}?parentExhaustion=${parentExhaustion}`,
    idMappings,
    accessToken
  );
}

export async function CheckByValidation(
  researchProjectId: string,
  configuredTransitionId: string,
  idMappings: ReadonlyArray<SampleTrackingCheckByValidation>,
  accessToken?: string
): Promise<ReadonlyArray<SampleTrackingCheckByValidationResult>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleTracking/CheckBy/Validate/ResearchProjectId/${researchProjectId}/Step/${configuredTransitionId}`,
    idMappings,
    accessToken
  );
}

export async function CheckByFastQValidation(
  idMappings: ReadonlyArray<SampleTrackingCheckByFastqValidation>,
  accessToken?: string
): Promise<ReadonlyArray<SampleTrackingCheckByFastqValidationResultPayload>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleTracking/CheckBy/ValidateFastQs`,
    idMappings,
    accessToken
  );
}

export async function GetInformaticsQcMetricsAsync(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<InformaticsQcMetrics>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleTracking/InformaticsQualityCheckMetrics`,
    sampleIds,
    accessToken
  );
}
